import { FrameData } from '../types';

/**
 * THE OMNI-SPLITTER (Browser Implementation)
 * 
 * 1. Takes raw video file.
 * 2. Extracts frames at defined intervals (default 1.5s).
 * 3. Returns a Sync-Map skeleton with audio snippets.
 */

export const extractFramesFromVideo = async (
  videoFile: File,
  intervalSeconds: number = 1.5,
  onProgress: (progress: number) => void
): Promise<FrameData[]> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const frames: FrameData[] = [];
    const videoUrl = URL.createObjectURL(videoFile);

    if (!context) {
      reject(new Error("Canvas context not available"));
      return;
    }

    video.src = videoUrl;
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = async () => {
      canvas.width = video.videoWidth / 2; // Scale down for performance/API limits
      canvas.height = video.videoHeight / 2;
      const duration = video.duration;
      let currentTime = 0;

      const captureFrame = async () => {
        if (currentTime >= duration) {
          URL.revokeObjectURL(videoUrl);
          resolve(frames);
          return;
        }

        // Seek
        video.currentTime = currentTime;
      };

      video.onseeked = () => {
        // Draw
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageUrl = canvas.toDataURL('image/jpeg', 0.7);

        frames.push({
          id: crypto.randomUUID(),
          timestamp: currentTime,
          imageUrl: imageUrl,
          status: 'pending'
        });

        const progress = Math.min((currentTime / duration) * 100, 100);
        onProgress(progress);

        // Advance
        currentTime += intervalSeconds;
        captureFrame();
      };

      video.onerror = (e) => {
        URL.revokeObjectURL(videoUrl);
        reject(e);
      };

      // Start loop
      captureFrame();
    };
  });
};

/**
 * Extract audio from video file and convert to base64
 * Uses Web Audio API for browser-based extraction
 */
export const extractAudioFromVideo = async (videoFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Convert to WAV format
        const wavBlob = audioBufferToWav(audioBuffer);
        const base64 = await blobToBase64(wavBlob);
        
        resolve(base64);
      } catch (error) {
        console.error('Audio extraction error:', error);
        resolve(''); // Return empty on error
      }
    };
    
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(videoFile);
  });
};

/**
 * Extract a specific audio snippet from video
 */
export const extractAudioSnippet = async (
  videoFile: File, 
  startTime: number, 
  duration: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Calculate sample positions
        const sampleRate = audioBuffer.sampleRate;
        const startSample = Math.floor(startTime * sampleRate);
        const endSample = Math.min(
          Math.floor((startTime + duration) * sampleRate),
          audioBuffer.length
        );
        const snippetLength = endSample - startSample;
        
        if (snippetLength <= 0) {
          resolve('');
          return;
        }
        
        // Create new buffer for snippet
        const snippetBuffer = audioContext.createBuffer(
          audioBuffer.numberOfChannels,
          snippetLength,
          sampleRate
        );
        
        // Copy audio data
        for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
          const sourceData = audioBuffer.getChannelData(channel);
          const targetData = snippetBuffer.getChannelData(channel);
          for (let i = 0; i < snippetLength; i++) {
            targetData[i] = sourceData[startSample + i];
          }
        }
        
        // Convert to WAV
        const wavBlob = audioBufferToWav(snippetBuffer);
        const base64 = await blobToBase64(wavBlob);
        
        resolve(base64);
      } catch (error) {
        console.error('Audio snippet extraction error:', error);
        resolve('');
      }
    };
    
    reader.onerror = () => resolve('');
    reader.readAsArrayBuffer(videoFile);
  });
};

/**
 * Convert AudioBuffer to WAV Blob
 */
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);
  
  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);
  
  // Write audio data
  const offset = 44;
  const channels: Float32Array[] = [];
  for (let i = 0; i < numChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }
  
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, channels[channel][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset + (i * blockAlign) + (channel * bytesPerSample), intSample, true);
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Return just the base64 part without the data URL prefix
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}